import { useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { HeadingSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useAboutContext } from '@/contexts/AboutContext'
import { ProjectTile } from '@/molecules/ProjectTile'
import { useTranslate } from '@/utils/translate/translate-client'

const NO_HUB_PAGE_PROJECT_SLUGS = [slugs.dryBar, slugs.forYourConsideration, slugs.insideAngelStudios, slugs.theChosen]

interface GetProjectTileHrefProps {
  projectType: string
  slug: string
}

function getProjectTileHref({ projectType, slug }: GetProjectTileHrefProps) {
  let href

  if (!NO_HUB_PAGE_PROJECT_SLUGS.includes(slug)) {
    if (projectType === 'movie') {
      href = `/movies/${slug}`
    } else if (projectType === 'series') {
      href = `/shows/${slug}`
    }
  }

  return href
}

export const OurTitlesSection = () => {
  const { tiles } = useAboutContext()
  const { t } = useTranslate('about')
  const [seeMore, setSeeMore] = useState(false)

  const seeMoreText = t('seeMore', 'See More')
  const seeLessText = t('seeLess', 'See Less')

  const handleClick = () => {
    setSeeMore(!seeMore)
  }

  return (
    <section className="bg-core-gray-950 py-[72px]" id="titles">
      <HeadingSM className="photon-heading-xl mx-auto max-w-[238px] pb-8 text-center lg:max-w-none" weight="bold">
        {t('ourMoviesAndShows', 'Our Movies + Shows')}
      </HeadingSM>
      <div className="px-8 xl:grid xl:grid-cols-12">
        <ResponsiveFlexRow className="col-span-10 col-start-2 mx-auto flex-wrap items-center justify-center gap-8">
          {tiles.projects
            ?.sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
            ?.map((project, index) => {
              if (
                project?.public &&
                project?.discoveryPosterCloudinaryPath &&
                project?.discoveryPosterTransformation &&
                project?.name &&
                project?.projectType &&
                project?.slug &&
                project?.pifEnabled
              ) {
                const imageUrl =
                  project?.title?.portraitAngelImage?.cloudinaryPath ??
                  project?.title?.portraitTitleImage?.cloudinaryPath ??
                  project?.discoveryPosterCloudinaryPath
                const showLogo = project?.slug === slugs.dryBar || project?.slug === slugs.forYourConsideration

                return (
                  <div className={classNames({ 'hidden md:block': !seeMore && index > 3 })} key={project.slug}>
                    <ProjectTile
                      discoveryPosterCloudinaryPath={imageUrl}
                      discoveryPosterTransformation={showLogo ? project?.discoveryPosterTransformation : ''}
                      href={getProjectTileHref({ projectType: project.projectType, slug: project.slug })}
                      logoCloudinaryPath={showLogo ? project?.logoCloudinaryPath : null}
                      name={project.name}
                      slug={project.slug}
                      projectType={project.projectType}
                      sortOrder={index}
                    />
                  </div>
                )
              }

              return null
            })}
        </ResponsiveFlexRow>
      </div>
      <div className="px-8 pt-8 md:hidden">
        <Button className="h-14 w-full" onClick={handleClick} variant="core-gray-900">
          {seeMore ? seeLessText : seeMoreText}
        </Button>
      </div>
    </section>
  )
}
